import { useAuth } from '@contexts/AuthContext';
import authTextureBg from '@assets/auth-texture-bg.webp';
import signupProductFrame from '@assets/signup-product-frame.webp';
import percuityWordmark from '@assets/percuity-wordmark.png';
import { LANDING_PAGE_URL } from '@config/config';
import { useFeatureFlagVariant } from '@hooks/useFeatureFlagVariant';
import { Layout } from 'antd';
import { TickSquare } from 'iconsax-react';

import { Navigate, Outlet, useLocation } from 'react-router';

const SIGNUP_HERO_FLAG = 'signup-hero-sidebar';

const HERO_CAPABILITIES = [
  'Plan winning campaigns with AI strategy',
  'Generate on-brand creative in seconds',
  'Launch ads across every major platform',
  'Auto-optimize budgets and bids 24/7',
  'See unified reporting across every channel',
];
const { Content } = Layout;

const AuthLayout = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const normalizedPathname = location.pathname.replace(/\/+$/, '');

  const isCompletingProfile =
    searchParams.get('federated_signup_success') === '1' &&
    searchParams.get('complete_profile') === '1';

  const isCompletingSignupStep =
    normalizedPathname === '/auth/signup' && searchParams.get('step');

  const shouldRedirectAuthenticated =
    isAuthenticated &&
    !isCompletingProfile &&
    !isCompletingSignupStep;

  const isSignup = location.pathname.includes('signup');
  const heroVariant = useFeatureFlagVariant(SIGNUP_HERO_FLAG);
  const showSignupHero = isSignup && heroVariant === 'test';

  return shouldRedirectAuthenticated ? (
    <Navigate to="/" replace />
  ) : (
    <Layout
      className="justify-center items-center flex"
      style={{ backgroundColor: 'var(--color-white)' }}
    >
      <Content className="w-full flex h-screen flex-col md:flex-row md:overflow-hidden overflow-y-auto mx-auto">
        {showSignupHero && (
          <div className="flex-1 hidden md:flex relative h-full overflow-hidden bg-black">
            <img
              src={authTextureBg}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-[1] contrast-[0.8] brightness-[0.4]"
            />
            <div className="flex flex-col w-full h-full p-8 pb-0 gap-4">
              <a
                href={LANDING_PAGE_URL}
                aria-label="Percuity home"
                className="z-10 mb-8"
              >
                <img
                  src={percuityWordmark}
                  alt="Percuity"
                  className="h-6 brightness-0 invert"
                />
              </a>
              <div className="z-10 text-white 2xl:hidden">
                <h1
                  className="text-2xl lg:text-2xl leading-tight tracking-tight"
                  style={{
                    fontFamily: '"Host Grotesk", "Manrope", sans-serif',
                    fontWeight: 800,
                  }}
                >
                  Run your entire ad operation in one place.
                </h1>
                <ul className="mt-5 flex flex-col gap-2 text-sm text-leo-secondary">
                  {HERO_CAPABILITIES.map((capability) => (
                    <li key={capability} className="flex items-center gap-2">
                      <TickSquare
                        size={14}
                        variant="Outline"
                        color="currentColor"
                      />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative flex-grow min-h-0">
                <div className="z-10 text-white 2xl:w-[1000px] 2xl:block hidden mx-auto">
                  <h1
                    className="text-2xl lg:text-2xl leading-tight tracking-tight"
                    style={{
                      fontFamily: '"Host Grotesk", "Manrope", sans-serif',
                      fontWeight: 800,
                    }}
                  >
                    Run your entire ad operation in one place.
                  </h1>
                  <ul className="mt-5 flex flex-col gap-2 text-sm text-leo-secondary">
                    {HERO_CAPABILITIES.map((capability) => (
                      <li key={capability} className="flex items-center gap-2">
                        <TickSquare
                          size={14}
                          variant="Outline"
                          color="currentColor"
                        />
                        <span>{capability}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="absolute z-10 w-[1000px] bottom-[-84px] right-[40px] 2xl:right-1/2 2xl:translate-x-1/2 max-w-none rounded-2xl bg-gradient-to-b from-neutral-600 to-neutral-700 p-[16px] shadow-[0_-20px_40px_rgba(0,0,0,0.25)] ring-1 ring-white/20">
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
                  />
                  <span
                    aria-hidden="true"
                    className="absolute top-[6px] left-1/2 -translate-x-1/2 block h-[4px] w-[4px] rounded-full bg-neutral-950 ring-1 ring-white/15"
                  />
                  <img
                    src={signupProductFrame}
                    alt=""
                    aria-hidden="true"
                    className="block w-full h-auto rounded-md ring-1 ring-black/40"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 flex flex-col h-full md:overflow-y-auto relative">
          <a
            href={LANDING_PAGE_URL}
            aria-label="Percuity home"
            className={`absolute top-8 left-8 z-10 ${showSignupHero ? 'md:hidden' : ''}`}
          >
            <img
              src={percuityWordmark}
              alt="Percuity"
              className="h-6 brightness-0"
            />
          </a>
          <div className="flex-1 flex items-center justify-center w-full px-5 py-20">
            <div
              className={`w-full max-w-[460px] p-8 md:p-10 ${showSignupHero ? '' : 'bg-white rounded-xl border border-gray-200'}`}
            >
              <Outlet />
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default AuthLayout;
